import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from 'react-router-dom';
import Routes from 'routes';
import ThemeCustomization from 'themes';
import ScrollTop from 'components/ScrollTop';
import authService from 'store/services/auth.service';
import { ThemeProvider, useThemeContext } from 'themes/ThemeContext';
import { useGetTenancyBasicsQuery } from 'store/services/tenancy.service';
import { isValidHexCode } from 'utils/colorUtils';
import { setIsHierarchyTenancy } from 'store/reducers/tenancy';
import Maintenance from 'pages/extra-pages/Maintenance';

const AppContent = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { customColors, themeMode, updateThemeColors } = useThemeContext();
    const { tenancyId } = useSelector((state) => state.tenancy);
    const { requiresMfaSetup } = useSelector((state) => state.auth);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [hasUpdatedColors, setHasUpdatedColors] = useState(false);
    const lastNavigationRef = useRef(null);
    const user = isAuthenticated ? JSON.parse(localStorage.getItem('user')) : null;

    // query for redux state tenancy
    const { data: tenancyBasics, isLoading } = useGetTenancyBasicsQuery(
        { tenancyId },
        { skip: !isAuthenticated || !tenancyId }
    );

    // User's original tenancy query - this is to validate their isHierachyTenancy value for the tenancy they belong to
    const { data: userTenancyBasics, isLoading: isLoadingUserTenancy } = useGetTenancyBasicsQuery(
        { tenancyId: user?.tenancyId },
        {
            skip: !isAuthenticated || !user?.tenancyId || user?.role === 'Administrators'
        }
    );

    const updateColors = useCallback(() => {
        if (tenancyBasics && !isLoading && !hasUpdatedColors) {
            const newColors = {
                primary: isValidHexCode(tenancyBasics.primaryHex) ? tenancyBasics.primaryHex : customColors.primary,
                secondary: isValidHexCode(tenancyBasics.secondaryHex) ? tenancyBasics.secondaryHex : customColors.secondary,
                warning: isValidHexCode(tenancyBasics.tertiaryHex) ? tenancyBasics.tertiaryHex : customColors.warning
            };
            updateThemeColors(newColors);
            setHasUpdatedColors(true);
        }
    }, [tenancyBasics, isLoading, hasUpdatedColors, updateThemeColors, customColors]);

    useEffect(() => {
        const storedUser = localStorage.getItem('user');
        if (storedUser && isAuthenticated) { // Add isAuthenticated check
            const userData = JSON.parse(storedUser);
            const needsMfaSetup = userData.requiresMfaSetup || requiresMfaSetup;

            if (needsMfaSetup && location.pathname !== '/mfa-setup') {
                console.log('App.js: Redirecting to MFA setup (backup check)');
                navigate('/mfa-setup', { replace: true });
            }
        }
    }, [isAuthenticated, requiresMfaSetup, location.pathname, navigate]);

    useEffect(() => {
        const checkAuth = () => {
            console.log('=== APP.JS checkAuth ===');
            console.log('App: Current path:', location.pathname);
            console.log('App: localStorage user exists:', !!localStorage.getItem('user'));

            const loggedIn = authService.isLoggedIn();
            console.log('App: isLoggedIn result:', loggedIn);
            setIsAuthenticated(loggedIn);

            const publicPaths = [
                '/login',
                '/forgot-password',
                '/login/reset-password',
                '/universitycourse/takecourse',
                '/campaignclicked',
                '/landingpage',
                '/sso/',
                '/oktasso_initiate',
                '/oktasso',
                '/uss-login',
                '/cn-sso',
                '/google-sso/',
                '/aliaslogin',
                '/mfa-setup',
                '/login-mfa'
            ];

            const currentPath = location.pathname;
            const isPublicPath = publicPaths.some(path =>
                path.endsWith('/') ? currentPath.startsWith(path) : currentPath === path || currentPath.startsWith(path + '/')
            );

            console.log('App: isPublicPath:', isPublicPath);
            console.log('App: lastNavigationRef:', lastNavigationRef.current);

            if (!loggedIn && !isPublicPath && lastNavigationRef.current !== '/login') {
                console.log('App: >>> REDIRECTING TO /login <<<');
                lastNavigationRef.current = '/login';
                navigate('/login');
            } else {
                console.log('App: No redirect needed');
            }
        };
        checkAuth();
    }, [navigate, location.pathname]);

    useEffect(() => {
        if (isAuthenticated && user && tenancyBasics && !isLoading) {
            if (user.role === 'Administrators' || !userTenancyBasics) {
                dispatch(setIsHierarchyTenancy(tenancyBasics.isHierarchyTenancy));
            } else if (userTenancyBasics) {
                dispatch(setIsHierarchyTenancy(userTenancyBasics.isHierarchyTenancy));
            }
        }
    }, [tenancyBasics, userTenancyBasics, isLoading, isAuthenticated, user, dispatch]);

    useEffect(() => {
        updateColors();
    }, [updateColors]);

    // Check for maintenance mode
    const isMaintenanceMode = process.env.REACT_APP_MAINTENANCE_MODE === 'true';

    // If maintenance mode is enabled, show maintenance page
    if (isMaintenanceMode) {
        return (
            <ThemeCustomization customColors={customColors} mode={themeMode}>
                <Maintenance />
            </ThemeCustomization>
        );
    }

    return (
        <ThemeCustomization customColors={customColors} mode={themeMode}>
            <ScrollTop>
                <Routes />
            </ScrollTop>
        </ThemeCustomization>
    );
};

const App = () => {
    return (
        <ThemeProvider>
            <AppContent />
        </ThemeProvider>
    );
};

export default App;